import { useState, useEffect, useMemo } from "react";
import { Layout } from "../components/Layout";
import { apiClient, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Search,
  X,
  Trash2,
  RotateCcw,
  Loader2,
  Trash,
} from "lucide-react";

const getTagStyle = (color) => ({
  backgroundColor: `${color}20`,
  color: color,
  borderColor: `${color}40`,
});

const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const originLabels = { todo: "ToDo", kunden: "Kunden" };

export default function TrashPage() {
  const { isAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { trash: true };
      if (search) params.search = search;
      const res = await apiClient.get("/customers", { params });
      setCustomers(res.data);
    } catch (error) {
      toast.error("Fehler beim Laden des Papierkorbs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [search]);

  const sorted = useMemo(
    () => [...customers].sort((a, b) => new Date(b.deleted_at || 0) - new Date(a.deleted_at || 0)),
    [customers]
  );

  const handleRestore = async (customer) => {
    setBusyId(customer.id);
    try {
      await apiClient.post(`/customers/${customer.id}/restore`);
      toast.success(`Wiederhergestellt in ${originLabels[customer.deleted_from] || "Kunden"}`);
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Wiederherstellen");
    } finally {
      setBusyId(null);
    }
  };

  const handlePermanentDelete = async (customer) => {
    if (!window.confirm(`"${customer.name}" endgültig und unwiderruflich löschen? Dies kann nicht rückgängig gemacht werden.`)) return;
    setBusyId(customer.id);
    try {
      await apiClient.delete(`/customers/${customer.id}/permanent`);
      toast.success("Endgültig gelöscht");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim endgültigen Löschen");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Layout title="Papierkorb">
      <div className="flex flex-col sm:flex-row gap-4 mb-6" data-testid="trash-header">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Im Papierkorb suchen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 pr-10 h-11 border-slate-200"
            data-testid="trash-search-input"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded-full transition-colors"
              data-testid="trash-clear-search-btn"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="trash-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50">
                <TableHead className="font-semibold text-slate-600">Name</TableHead>
                <TableHead className="font-semibold text-slate-600">Firma</TableHead>
                <TableHead className="font-semibold text-slate-600">Tags</TableHead>
                <TableHead className="font-semibold text-slate-600">Ursprung</TableHead>
                <TableHead className="font-semibold text-slate-600">Gelöscht am</TableHead>
                <TableHead className="font-semibold text-slate-600 text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Trash className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">Der Papierkorb ist leer</p>
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((customer) => (
                  <TableRow key={customer.id} data-testid={`trash-row-${customer.id}`}>
                    <TableCell className="font-medium text-slate-900">{customer.name}</TableCell>
                    <TableCell className="text-slate-600">{customer.company || "-"}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {customer.tags?.map((tag) => (
                          <Badge key={tag.id} variant="outline" className="text-xs" style={getTagStyle(tag.color)}>
                            {tag.name}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-slate-100 text-slate-600">
                        {originLabels[customer.deleted_from] || "Kunden"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm whitespace-nowrap">
                      {formatDateTime(customer.deleted_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestore(customer)}
                          disabled={busyId === customer.id}
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          data-testid={`restore-btn-${customer.id}`}
                        >
                          {busyId === customer.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-4 h-4 mr-1" />Wiederherstellen</>}
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePermanentDelete(customer)}
                            disabled={busyId === customer.id}
                            className="border-red-300 text-red-700 hover:bg-red-50"
                            data-testid={`permanent-delete-btn-${customer.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />Endgültig löschen
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
}
